import { ShipmentOrderLegRunForPlanEntity } from 'src/entities/ShipmentOrderLegRun.entity';

import { toHHmm } from './evaluate';
import { FilterColumnDef } from './types';

type LegRun = ShipmentOrderLegRunForPlanEntity;

/** boolean 列の表示文言。AdvancedFilterPopover の値セレクトと同じにする（検索語を UI から写せるように）。 */
const BOOLEAN_TEXTS: Record<'true' | 'false', string> = { true: '可', false: '不可' };

/**
 * 1 列ぶんの「検索に使える文字列」を取り出す。
 *
 * enumSelect / idSelect が値ではなくラベルを返すのが要点。指定トラックの実体は
 * ID の配列なので、値のまま検索すると「11」でしか引けず、画面に出ている
 * トラック名では引けない。
 */
export function searchableTexts(legRun: LegRun, column: FilterColumnDef): string[] {
  switch (column.type) {
    case 'text': {
      const raw = column.getValue(legRun);
      const list = Array.isArray(raw) ? raw : [raw];
      return list.filter((v): v is string => v != null && v !== '');
    }
    case 'number': {
      const value = column.getValue(legRun);
      return value == null ? [] : [String(value)];
    }
    case 'time': {
      // 生の ISO 文字列で引けても意味が無い。画面と同じ HH:mm で引けるようにする
      const hhmm = toHHmm(column.getValue(legRun));
      return hhmm ? [hhmm] : [];
    }
    case 'enumSelect': {
      const values = column.getValue(legRun) ?? [];
      const labelByValue = new Map(column.options.map((o) => [o.value, o.label]));
      return values.map((v) => labelByValue.get(v) ?? v);
    }
    case 'idSelect': {
      const values = column.getValue(legRun) ?? [];
      const labelByValue = new Map(column.options.map((o) => [o.value, o.label]));
      // 候補に無い ID は名前が出せない。ID を文字列で返すと「11」で引けてしまい
      // ラベル検索の意図から外れるので、落とす
      return values.map((v) => labelByValue.get(v)).filter((v): v is string => v != null);
    }
    case 'boolean': {
      const value = column.getValue(legRun);
      return value == null ? [] : [BOOLEAN_TEXTS[value ? 'true' : 'false']];
    }
    default:
      return [];
  }
}

/**
 * フリーテキスト検索。詳細フィルターで選べる列すべてを対象にする。
 *
 * Why not 降地名称だけ: 伝票No や荷主名で探したいときに、わざわざ詳細フィルターを
 * 開いて条件を組む必要があった。
 */
export function matchesFreeText(legRun: LegRun, needle: string, columns: FilterColumnDef[]): boolean {
  const n = needle.trim().toLowerCase();
  if (n === '') return true;
  return columns.some((column) =>
    searchableTexts(legRun, column).some((text) => text.toLowerCase().includes(n)));
}
